import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ResponseDto } from 'src/app/models/dto/responseDto';
import { BaseTestItemDto } from 'src/app/models/dto/Base-Test-item-dto';
import { formField } from 'src/app/models/formField';
import { TestSpecificationDto } from 'src/app/models/dto/testSpecificationDto';
import { ApolloClientService } from './apollo-client.service';
import { GETALL_TEST_SPECIFICATIONS_QUERY, GET_TEST_SPECIFICATION_QUERY, GET_TEST_SPECIFICATION_SEARCH_UI_TEMPLATE_QUERY, GET_TEST_SPECIFICATION_UI_TEMPLATE_QUERY, SEARCH_TEST_SPECIFICATION_QUERY } from 'src/app/graphQL/queries/test-specification-queries';
import { GraphQLBaseTestItemOutput } from 'src/app/graphQL/models/output/graphql-base-test-item-output';
import { convertGraphqlBaseTestItemToDto, convertGraphqlFormFieldToDto, convertGraphqlResponseToDto, convertGraphqlResponseToDtoSimpleReturnValue } from 'src/app/graphQL/mappers/common.mappers';
import { GraphQLResponseOutput } from 'src/app/graphQL/models/output/graphql-response-output';
import { GraphQLFormFieldOutput } from 'src/app/graphQL/models/output/graphql-form-field-output';
import { convertTestSpecificationDtoToGraphqlInput } from 'src/app/graphQL/mappers/test-specification.mapper';
import { CREATE_TEST_SPECIFICATION_MUTATION, DELETE_TEST_SPECIFICATION_MUTATION, UPDATE_TEST_SPECIFICATION_MUTATION } from 'src/app/graphQL/mutations/test-specification-mutations';
import { DualApiService, Scope } from '../migration/dual-api.service';

@Injectable({
  providedIn: 'root'
})
export class TestSpecificationApiService {

  constructor(private apolloClientService: ApolloClientService, private dualApiService: DualApiService) { }

  getAll(company: string, regBy: string, functionalGroupId?: string): Observable<BaseTestItemDto[]> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorAllTestSpecifications : GraphQLBaseTestItemOutput[]}>({
          query: GETALL_TEST_SPECIFICATIONS_QUERY,
          variables: {Company: company, RegBy: regBy, OrgGroup: functionalGroupId}
        })
      ),
      map(results => {
        const testSpecifications = results.data.configuratorAllTestSpecifications
        if (!testSpecifications)
          return undefined

        return testSpecifications.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    );

    let scope: Scope | undefined;
    if (regBy) scope = 'MINE';
    else if (functionalGroupId) scope = 'MY_GROUP';

    return this.dualApiService.dualGetAll('testSpecifications', [], oldApiCall$, scope);
  }

  getByUid(uid: string): Observable<ResponseDto> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorTestSpecificationById: GraphQLResponseOutput}>({
          query: GET_TEST_SPECIFICATION_QUERY,
          variables: {Uid: uid}
        })
      ),
      map(results => {
        const testSpecification = results.data.configuratorTestSpecificationById
        if (!testSpecification)
          return undefined

        return convertGraphqlResponseToDto<TestSpecificationDto>(testSpecification);
      })
    );

    return this.dualApiService.dualGetByUid('testSpecifications', uid, oldApiCall$);
  }

  getUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorTestSpecificationUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_TEST_SPECIFICATION_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const testSpecificationUiTemplate = results.data.configuratorTestSpecificationUiTemplate
        if (!testSpecificationUiTemplate)
          return undefined

        return testSpecificationUiTemplate.map(item => {
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }

  getSearchUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorTestSpecificationSearchUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_TEST_SPECIFICATION_SEARCH_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const testSpecificationUiTemplate = results.data.configuratorTestSpecificationSearchUiTemplate

        if (!testSpecificationUiTemplate)
          return undefined

        return testSpecificationUiTemplate.map(item => {
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }

  create(testSpecification: TestSpecificationDto): Observable<ResponseDto> {
    const graphqlTestSpecificationInput = convertTestSpecificationDtoToGraphqlInput(testSpecification)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorCreateTestSpecification: GraphQLResponseOutput}>({
          mutation: CREATE_TEST_SPECIFICATION_MUTATION,
          variables: {TestSpecification: graphqlTestSpecificationInput }
        })
      ),
      map(result => convertGraphqlResponseToDtoSimpleReturnValue(result.data.configuratorCreateTestSpecification as GraphQLResponseOutput))
    );

    return this.dualApiService.dualCreate('testSpecifications', testSpecification, oldApiCall$);
  }

  update(testSpecification: TestSpecificationDto): Observable<ResponseDto> {
    const graphqlTestSpecificationInput = convertTestSpecificationDtoToGraphqlInput(testSpecification)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorUpdateTestSpecification: GraphQLResponseOutput}>({
          mutation: UPDATE_TEST_SPECIFICATION_MUTATION,
          variables: {TestSpecification: graphqlTestSpecificationInput }
        })
      ),
      map(result => convertGraphqlResponseToDtoSimpleReturnValue(result.data.configuratorUpdateTestSpecification as GraphQLResponseOutput))
    );

    return this.dualApiService.dualUpdate('testSpecifications', testSpecification.uid, testSpecification, oldApiCall$);
  }

  delete(uid: string, regBy: string): Observable<any> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate({
          mutation: DELETE_TEST_SPECIFICATION_MUTATION,
          variables: {Uid: uid}
        })
      ),
      map(result =>(result.data))
    );

    return this.dualApiService.dualDelete('testSpecifications', uid, oldApiCall$);
  }

  search(testSpecification: TestSpecificationDto, scope?: Scope): Observable<BaseTestItemDto[]> {
    const graphqlTestSpecificationInput = convertTestSpecificationDtoToGraphqlInput(testSpecification);

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorSearchTestSpecification: GraphQLBaseTestItemOutput[]}>({
          query: SEARCH_TEST_SPECIFICATION_QUERY,
          variables: {TestSpecification: graphqlTestSpecificationInput }
        })
      ),
      map(results => {
        const testSpecifications = results?.data?.configuratorSearchTestSpecification
        if (!testSpecifications)
          return undefined;

        return testSpecifications.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    );

    return this.dualApiService.dualSearch('testSpecifications', testSpecification, oldApiCall$, scope);
  }
}
